import type { EphemeralKeyCreateRequest, EphemeralKeyCreateResponse } from '../types';
import type { RequestFn } from '../client';

/**
 * Create short-lived ephemeral keys for secure client-side operations.
 *
 * Ephemeral keys grant a mobile or browser client temporary access to a
 * specific customer's data (e.g. to display saved payment methods) without
 * exposing your secret API key.
 *
 * The key is confined to the customer it was minted for, and that is
 * enforced on every customer and payment-method route: a request for another
 * customer — or for a payment method belonging to one — is refused rather
 * than served. Mint one key per customer; do not reuse a key across them.
 */
export class EphemeralKeys {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create an ephemeral key scoped to a specific customer.
   *
   * @param params - Customer ID and optional expiry.
   * @returns The ephemeral key with its plaintext secret and expiry timestamp.
   *
   * @example
   * ```typescript
   * const ephKey = await delopay.ephemeralKeys.create({ customer_id: 'cus_123' });
   * // Pass ephKey.secret to your mobile app.
   * ```
   */
  async create(params: EphemeralKeyCreateRequest): Promise<EphemeralKeyCreateResponse> {
    return this.request('POST', '/ephemeral-keys', { body: params });
  }

  /**
   * Invalidate an ephemeral key before it expires.
   *
   * @param keyId - The ephemeral key ID to delete.
   * @returns The deleted key object.
   */
  async delete(keyId: string): Promise<EphemeralKeyCreateResponse> {
    return this.request('DELETE', `/ephemeral-keys/${encodeURIComponent(keyId)}`);
  }
}
